import { IQuery } from 'core-ts-types';
import * as _4 from 'lodash4';
import { ORMMediator } from 'utility-ts-orm';
import { APUserModeConfig } from '../../global/components/user_mode_config/config/ap_user_mode_config';
import { APModel } from '../../global/models/ap_model';
import {
  SlotStatusCssClassColorMap,
  SlotStatusTextMap,
  SlotStatusTextToColorMap
} from '../../plan/components/slot_status_badge/servants/slot_status_badge_html_servant';
import { APAlternatePlanDetailsSlot } from '../builders/ap_alternate_plan_details_slot_builder';
import { APAlternatePlanListItem } from '../builders/ap_alternate_plan_list_item_builder';
import { IAPAlternatePlanListDatasource } from '../datasources/alternate_plan_list_datasource';
import {
  AlternatePlanComparePlansModel,
  IAlternatePlanComparePlanEntry
} from '../models/alternate_plan_compare_plans_model';

export interface IAPAlternatePlanComparePlanIndividualEntry {
  id: number;
  credit_range: string;
  slots: APAlternatePlanDetailsSlot[];
  is_current_plan: boolean;
  is_visible_to_student: boolean;
  is_selectable_by_student: boolean;
  taken_credits: number;
  remaining_credits: number;
  max_credits: number;
  name: string;
}

export interface IAPAlternatePlanComparePlanIndividualDetails {
  has_plan_exist: boolean;
  plan?: IAPAlternatePlanComparePlanIndividualEntry;
}

export interface IAPAlternatePlanComparePlanDetails {
  from_plan?: IAPAlternatePlanComparePlanIndividualDetails;
  to_plan?: IAPAlternatePlanComparePlanIndividualDetails;
  showBasePlanWarning: boolean;
  correctBasePlanName: string;
}

export class APAlternatePlanComparePlansQuery implements IQuery {
  private _result: IAPAlternatePlanComparePlanDetails = {
    from_plan: undefined,
    to_plan: undefined,
    showBasePlanWarning: false,
    correctBasePlanName: ''
  };

  constructor(
    private ngAlternatePlanComparePlansModel: AlternatePlanComparePlansModel,
    private ngAPAlternatePlanListDatasource: IAPAlternatePlanListDatasource,
    private ngAPUserModeConfig: APUserModeConfig,
    private ngAPModel: APModel
  ) {
    ORMMediator.registerQuery(this, ngAPModel, ngAlternatePlanComparePlansModel);
  }

  getCorrectBasePlanName() {
    let correctBasePlanName = '';
    const currentProgramId = _4.get(this.ngAPModel.currentProgram(), 'id', 0);
    if (currentProgramId) {
      const basePlansList = _4.filter(
        this.ngAPAlternatePlanListDatasource.read(),
        (alternatePlan) => alternatePlan.is_base_plan
      );
      const correctBasePlan = _4.first(_4.reject(basePlansList, { id: currentProgramId }));
      correctBasePlanName = _4.get(correctBasePlan, 'name', '');
    }
    return correctBasePlanName;
  }

  buildIndividualPlanDetails(planEntry: IAlternatePlanComparePlanEntry): IAPAlternatePlanComparePlanIndividualDetails {
    const currentProgram = this.ngAPModel.currentProgram();
    const hasPlanExist = _4.has(planEntry, 'id');
    if (!hasPlanExist) {
      return {
        has_plan_exist: false,
        plan: undefined
      };
    }
    const slots = _4.sortBy(planEntry.slots, 'slot').map((slot) => ({
      ...slot,
      title: slot.desc,
      badge: !_4.isEmpty(slot.status)
        ? {
            cssClass: SlotStatusCssClassColorMap[SlotStatusTextToColorMap[slot.status]],
            text: SlotStatusTextMap[slot.status]
          }
        : undefined,
      status: _4.includes(['REGISTERED', 'SECTION_PICKED', 'IN_PROGRESS'], slot.status) ? 'IN_PROGRESS' : slot.status
    }));
    let planDetailsInList = this.ngAPAlternatePlanListDatasource.get(planEntry.id) as APAlternatePlanListItem;
    if (!planDetailsInList) {
      planDetailsInList = APAlternatePlanListItem.build({
        taken_credits: 0,
        max_credits: 0,
        min_credits: 0,
        is_visible_to_student: false,
        is_selectable_by_student: false,
        name: ''
      });
    }
    return {
      plan: {
        id: planEntry.id,
        slots,
        taken_credits: planDetailsInList.taken_credits,
        max_credits: planDetailsInList.max_credits,
        remaining_credits: planDetailsInList.max_credits - planDetailsInList.taken_credits,
        credit_range:
          planDetailsInList.max_credits !== planDetailsInList.min_credits
            ? `${planDetailsInList.min_credits} - ${planDetailsInList.max_credits}`
            : `${planDetailsInList.min_credits}`,
        is_current_plan: currentProgram.id === planEntry.id,
        is_visible_to_student: _4.get(planDetailsInList, 'is_visible_to_student', false),
        is_selectable_by_student: _4.get(planDetailsInList, 'is_selectable_by_student', false),
        name: _4.get(planDetailsInList, 'name', '')
      },
      has_plan_exist: hasPlanExist
    };
  }
  getNumberOfBasePlans() {
    return _4.size(
      _4.filter(this.ngAPAlternatePlanListDatasource.read(), (alternatePlan) => alternatePlan.is_base_plan)
    );
  }

  run() {
    const comparePlans = this.ngAlternatePlanComparePlansModel.read();
    this._result.from_plan = this.buildIndividualPlanDetails(comparePlans.from as IAlternatePlanComparePlanEntry);
    this._result.to_plan = this.buildIndividualPlanDetails(comparePlans.to as IAlternatePlanComparePlanEntry);
    this._result.showBasePlanWarning = _4.gt(this.getNumberOfBasePlans(), 1);
    this._result.correctBasePlanName = this.getCorrectBasePlanName();
    return this;
  }

  get result() {
    return this._result;
  }
}

angular
  .module('capability-ng-academic-planner')
  .service('ngAPAlternatePlanComparePlansQuery', APAlternatePlanComparePlansQuery);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/alternate_plans/queries/alternate_plan_compare_plans_query.ts